<template>
  <content-dialog
    :visible.sync="modalVisible"
    :fullscreen="$vuetify.breakpoint.smAndDown"
    max-width="500px"
    :title-padding="$vuetify.breakpoint.xsOnly ? 'pa-2' : 'pa-4'"
  >
    <!--<editor-fold desc="Dialog Title">-->
    <template slot="dialogTitle">
      Enter additional details
    </template>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Dialog Content">-->
    <v-card-text :class="{'pa-4': $vuetify.breakpoint.smAndUp }">
      <div
        v-if="serviceRepositoryTask"
        class="caption grey--text text--darken-2"
      >
        <div class="font-weight-bold">
          {{ serviceRepositoryTask.title }}
        </div>
        <div v-html="serviceRepositoryTask.description" />
        <v-divider class="my-3" />
      </div>

      <div v-if="workLogs.length > 0">
        <div class="font-weight-bold mb-2">
          Previous Work
        </div>

        <div
          class="grey lighten-3 pa-1"
        >
          <v-row
            v-for="workLog in workLogs"
            :key="'workLog-'+workLog.id"
            class="list--bordered pa-1 caption"
            no-gutters
          >
            <v-col
              cols="auto"
              class="text-no-wrap"
            >
              <strong>{{ workLog.done_at | moment('D MMM YYYY') }}</strong>
            </v-col>
            <v-col class="px-3">
              {{ workLog.done_by.name }}
            </v-col>
            <v-col cols="auto">
              {{ workLog.hours_spent }}h
            </v-col>
          </v-row>
        </div>

        <v-divider class="my-3" />
      </div>

      <validation-observer ref="validationObserver">
        <v-switch
          v-model="finishTask"
          label="Mark annual service as done"
        />

        <validation-provider
          v-slot="{ errors }"
          rules="required|min_value:0.1"
          name="hours spent"
        >
          <v-text-field
            v-model="hoursSpentDirty"
            v-validate="'required|min_value:0.1'"
            :error-messages="errors"
            type="number"
            step="0.5"
            min="0"
            label="Hours Spent"
            placeholder="Enter hours spent..."
            outlined
          />
        </validation-provider>

        <validation-provider
          v-slot="{ errors }"
          rules="required"
          name="finished by"
        >
          <assignee-modal-select
            v-model="assignedToDirty"
            v-validate="'required'"
            :dialog-visible.sync="assigneeModalSelectVisible"
            :error-messages="errors"
            multiple
            outlined
            clearable
            small-chips
            deletable-chips
            label="Work Done By"
            @submit="assigneeModalSelectVisible = false"
          />
        </validation-provider>

        <v-textarea
          v-model="customDescription"
          label="Description"
          outlined
        />
      </validation-observer>
    </v-card-text>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Dialog Actions">-->
    <template slot="dialogActions">
      <v-spacer />
      <v-btn
        text
        color="grey"
        @click="modalVisible = false"
      >
        Cancel
      </v-btn>
      <v-btn
        color="primary"
        :loading="submitting"
        @click="submit()"
      >
        <v-icon
          small
          left
        >
          check
        </v-icon>
        {{ finishTask ? 'Add Work Log & Mark As Done' : 'Add Work Log' }}
      </v-btn>
    </template>
    <!--</editor-fold>-->
  </content-dialog>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import ServiceTask from '@/models/ServiceTask'
import ContentDialog from '@/components/helpers/Dialog'
import AssigneeModalSelect from '@/components/service/helpers/AssigneeModalSelect'
import validationScrollMixin from '@/mixins/validationScrollMixin'

export default {
  name: 'ServiceTaskWinteringFinishDialog',
  components: {
    AssigneeModalSelect,
    ContentDialog
  },
  mixins: [
    validationScrollMixin
  ],
  props: {
    visible: Boolean,
    serviceRepositoryTask: {
      type: Object,
      required: true
    },
    yacht: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      finishTask: true,
      assigneeModalSelectVisible: false,
      hoursSpentDirty: null,
      assignedToDirty: [],
      workLogs: [],
      submitting: false,
      serviceTask: undefined,
      customDescription: null
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    currentEmployee() {
      return this.$store.state.auth.user.staff
    }
  },
  watch: {
    visible(value) {
      if (!value) return

      this.fetchServiceTask()
    }
  },
  mounted() {
    this.fetchServiceTask()

    this.pushCurrentEmployee()
  },
  methods: {
    async submit() {
      let validate = await this.$refs.validationObserver.validate()

      if (!validate) {
        this.showAlertAndScrollToFirstError()
        return
      }

      this.submitting = true

      try {
        let data = {
          hours_spent: this.hoursSpentDirty,
          assigned_to: this.assignedToDirty.map(assignee => {
            return {
              type: assignee.type,
              id: assignee.id
            }
          }),
          service_tasks_repository_id: this.serviceRepositoryTask.id,
          finish: this.finishTask,
          description: this.customDescription
        }

        const serviceTask = await ServiceTask.api.postWinteringTask(this.yacht.id, data)

        this.$emit('serviceTask:created', serviceTask)

        if (serviceTask.finished) {
          this.$store.dispatch('alert', {
            text: 'Annual service marked as done!',
            color: 'success'
          })
        } else {
          this.$store.dispatch('alert', {
            text: 'Work successfully logged',
            color: 'success'
          })
        }

        this.modalVisible = false

        this.assignedToDirty = []
        this.pushCurrentEmployee()
        this.hoursSpentDirty = null
      } catch (e) {
        this.$store.dispatch('alert', {
          'text': 'Something went wrong',
          'color': 'error'
        })

        throw e
      } finally {
        this.submitting = false
      }
    },
    pushCurrentEmployee() {
      let employee = cloneDeep(this.currentEmployee)
      employee.type = ServiceTask.ASSIGNEE_TYPE_STAFF

      this.assignedToDirty.push(employee)
    },
    async fetchServiceTask() {
      try {
        let serviceTasks = await ServiceTask.api.fetchList({
          service_tasks_repository_id: this.serviceRepositoryTask.id,
          yacht_id: this.yacht.id,
          finished: false
        })

        if (serviceTasks.length > 0) {
          this.serviceTask = serviceTasks[0]
          this.customDescription = this.serviceTask.description
          this.fetchWorkLogs()
        }
      } catch (e) {

      }
    },
    async fetchWorkLogs() {
      if (!this.serviceTask) {
        this.workLogs = []
        return
      }

      try {
        const workLogs = await ServiceTask.api.fetchWorkLogs(this.serviceTask.id)

        this.workLogs = workLogs
      } catch (e) {
        throw e
      }
    }
  }
}
</script>
